/**
 * persistClientSettings — Local snapshot of volume + keybinds + ping prefs
 * (SPY-313 / SPY-413 / FID-HUD-01). Presentation-only; domain stays pure.
 */

import { normalizeCameraLockPreference } from '../../domain/camera.js';

export const CLIENT_SETTINGS_STORAGE_KEY = 'lol.clientSettings.v1';

const PING_TYPE_CYCLE = Object.freeze([
  'danger', 'on_my_way', 'assist', 'missing'
]);

function readRaw(storage) {
  if (!storage || typeof storage.getItem !== 'function') return null;
  try {
    return storage.getItem(CLIENT_SETTINGS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function normalizeKeybinds(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out = {};
  for (const [action, key] of Object.entries(raw)) {
    if (typeof action !== 'string' || action.length === 0) continue;
    if (key === undefined || key === null) continue;
    out[action] = String(key);
  }
  return Object.keys(out).length ? out : undefined;
}

/** Clamp volume to 0..1 (SPY-1013). */
function normalizeVolume(n) {
  if (!Number.isFinite(n)) return undefined;
  return Math.min(1, Math.max(0, n));
}

/** MINIMAP cycle length 4 → indices 0..3. Reject non-integers / negatives; clamp highs. */
function normalizePingCycleIndex(n) {
  if (!Number.isInteger(n) || n < 0) return undefined;
  return Math.min(3, Math.max(0, n));
}

/** Clamp defaultPingType to the minimap cycle set (SPY-913). */
function normalizeDefaultPingType(raw) {
  if (typeof raw !== 'string' || !raw.length) return undefined;
  return PING_TYPE_CYCLE.includes(raw) ? raw : undefined;
}

const SCOREBOARD_SORT_CYCLE = Object.freeze(['team', 'gold', 'kda']);

/** Clamp scoreboardSort to known keys (FID-HUD-03). */
function normalizeScoreboardSort(raw) {
  if (typeof raw !== 'string' || !raw.length) return undefined;
  return SCOREBOARD_SORT_CYCLE.includes(raw) ? raw : undefined;
}

/**
 * @param {{ getItem?: Function }|null} [storage]
 * @returns {{ masterVolume: number, sfxVolume: number, keybinds?: Record<string, string> }|null}
 */
export function loadClientSettingsSnapshot(storage) {
  const raw = readRaw(storage);
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const snapshot = {};
    const masterVolume = normalizeVolume(parsed.masterVolume);
    const sfxVolume = normalizeVolume(parsed.sfxVolume);
    const musicVolume = normalizeVolume(parsed.musicVolume);
    if (masterVolume !== undefined) snapshot.masterVolume = masterVolume;
    if (sfxVolume !== undefined) snapshot.sfxVolume = sfxVolume;
    if (musicVolume !== undefined) snapshot.musicVolume = musicVolume;
    const keybinds = normalizeKeybinds(parsed.keybinds);
    if (keybinds) snapshot.keybinds = keybinds;
    const defaultPingType = normalizeDefaultPingType(parsed.defaultPingType);
    if (defaultPingType) snapshot.defaultPingType = defaultPingType;
    const pingCycleIndex = normalizePingCycleIndex(parsed.pingCycleIndex);
    if (pingCycleIndex !== undefined) snapshot.pingCycleIndex = pingCycleIndex;
    const scoreboardSort = normalizeScoreboardSort(parsed.scoreboardSort);
    if (scoreboardSort) snapshot.scoreboardSort = scoreboardSort;
    const cameraLock = normalizeCameraLockPreference(parsed.cameraLock);
    if (cameraLock !== undefined) snapshot.cameraLock = cameraLock;
    return snapshot;
  } catch {
    return null;
  }
}

/**
 * @param {{ setItem?: Function }|null} [storage]
 * @param {{ masterVolume?: number, sfxVolume?: number, keybinds?: Record<string, string> }|null} [snapshot]
 */
export function saveClientSettingsSnapshot(storage, snapshot) {
  if (!storage || typeof storage.setItem !== 'function' || !snapshot) return false;
  try {
    const payload = {};
    const masterVolume = normalizeVolume(snapshot.masterVolume);
    const sfxVolume = normalizeVolume(snapshot.sfxVolume);
    const musicVolume = normalizeVolume(snapshot.musicVolume);
    if (masterVolume !== undefined) payload.masterVolume = masterVolume;
    if (sfxVolume !== undefined) payload.sfxVolume = sfxVolume;
    if (musicVolume !== undefined) payload.musicVolume = musicVolume;
    const keybinds = normalizeKeybinds(snapshot.keybinds);
    if (keybinds) payload.keybinds = keybinds;
    const defaultPingType = normalizeDefaultPingType(snapshot.defaultPingType);
    if (defaultPingType) payload.defaultPingType = defaultPingType;
    const pingCycleIndex = normalizePingCycleIndex(snapshot.pingCycleIndex);
    if (pingCycleIndex !== undefined) payload.pingCycleIndex = pingCycleIndex;
    const scoreboardSort = normalizeScoreboardSort(snapshot.scoreboardSort);
    if (scoreboardSort) payload.scoreboardSort = scoreboardSort;
    const cameraLock = normalizeCameraLockPreference(snapshot.cameraLock);
    if (cameraLock !== undefined) payload.cameraLock = cameraLock;
    storage.setItem(CLIENT_SETTINGS_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/**
 * Merge partial into existing local snapshot (FID-HUD-01).
 * Keeps volume/keybinds when only ping fields change (and vice versa).
 * @param {{ getItem?: Function, setItem?: Function }|null} [storage]
 * @param {object|null} [partial]
 */
export function mergeClientSettingsSnapshot(storage, partial) {
  if (!partial || typeof partial !== 'object') return false;
  const prev = loadClientSettingsSnapshot(storage) || {};
  return saveClientSettingsSnapshot(storage, { ...prev, ...partial });
}
